/**
 * /system-admin — dashboard page (A6.1).
 *
 * Shows 4 metric cards (pending users, pending orgs, system admins, recent
 * audit count) plus a compact recent-audit list. PageHeader onReload refetches
 * all 4 queries simultaneously. Health endpoint unhealthy state (503) renders
 * an inline warning banner instead of a generic toast (meta.skipErrorToast is
 * set on the health query).
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import {
  LayoutDashboard,
  Users,
  Building2,
  ShieldCheck,
  ScrollText,
  AlertTriangle,
  Globe,
  UserRound,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardBody } from "@/components/ui/card";
import { DashboardCard } from "@/features/system-admin/components/dashboard/dashboard-card";
import { useSystemUsers } from "@/features/system-admin/api/users";
import { useSystemOrgs } from "@/features/system-admin/api/orgs";
import { useSystemHealth } from "@/features/system-admin/api/health";
import { useSystemAudit } from "@/features/system-admin/api/audit";
import { formatAuditActionLabel } from "@/features/system-admin/components/audit/audit-action-label";
import {
  resolveSystemAuditTarget,
  resolveSystemAuditTargetSubtitle,
} from "@/features/system-admin/utils/audit-display";
import { formatUserName } from "@/utils/format-user-name";
import { useIsMobile } from "@/hooks/useIsMobile";

export const Route = createFileRoute("/system-admin/")({
  component: SystemAdminDashboard,
});

/** Format an ISO timestamp to a compact locale string. */
function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function SystemAdminDashboard() {
  const t = useTranslations("system-admin.common");
  const td = useTranslations("system-admin.dashboard");
  const ta = useTranslations("system-admin.audit");
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Fetch counts — pageSize:1 to minimise payload; only `paging.total` is used.
  const pendingUsers = useSystemUsers({ status: "pending_approval", pageSize: 1 });
  const pendingOrgs = useSystemOrgs({ status: "pending", pageSize: 1 });
  const health = useSystemHealth();
  const recentAudit = useSystemAudit({ pageSize: 5 });

  const isHealthUnhealthy =
    !health.isLoading && (health.error !== null || health.data?.data?.bootstrapOk === false);

  const adminCount = health.data?.data?.adminCount ?? 0;
  const rootCount = health.data?.data?.rootCount ?? 0;
  const totalAdmins = adminCount + rootCount;
  const adminsSubLabel =
    !health.isLoading && health.data
      ? `${adminCount} ${td("cards.systemAdmins.statAdmin")} · ${rootCount} ${td("cards.systemAdmins.statRoot")}`
      : undefined;

  async function handleReload() {
    await Promise.all([
      pendingUsers.refetch(),
      pendingOrgs.refetch(),
      health.refetch(),
      recentAudit.refetch(),
    ]);
  }

  const isReloading =
    pendingUsers.isFetching ||
    pendingOrgs.isFetching ||
    health.isFetching ||
    recentAudit.isFetching;

  const auditRows = recentAudit.data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutDashboard}
        title={t("pages.dashboard.title")}
        description={t("pages.dashboard.subtitle")}
        onReload={handleReload}
        isReloading={isReloading}
      />

      {/* Health unhealthy banner */}
      {isHealthUnhealthy && (
        <div className="flex items-start gap-3 rounded-xl border border-[var(--destructive,theme(colors.red.500))]/30 bg-[var(--destructive,theme(colors.red.500))]/5 px-4 py-3 text-sm">
          <AlertTriangle
            className="mt-0.5 size-4 shrink-0 text-[var(--destructive,theme(colors.red.500))]"
            aria-hidden
          />
          <div>
            <p className="font-semibold text-[var(--foreground)]">{td("health.unhealthy.title")}</p>
            <p className="mt-0.5 text-[var(--muted-foreground)]">{td("health.unhealthy.body")}</p>
          </div>
        </div>
      )}

      {/* Metric cards grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <DashboardCard
          icon={Users}
          label={td("cards.pendingUsers.label")}
          value={pendingUsers.data?.paging.total}
          isLoading={pendingUsers.isLoading}
          accent="warning"
          onClick={() => void navigate({ to: "/system-admin/users" })}
        />

        <DashboardCard
          icon={Building2}
          label={td("cards.pendingOrgs.label")}
          value={pendingOrgs.data?.paging.total}
          isLoading={pendingOrgs.isLoading}
          accent="warning"
          onClick={() => void navigate({ to: "/system-admin/orgs" })}
        />

        <DashboardCard
          icon={ShieldCheck}
          label={td("cards.systemAdmins.label")}
          value={health.isLoading ? undefined : totalAdmins}
          subLabel={adminsSubLabel}
          isLoading={health.isLoading}
          accent="info"
          onClick={() => void navigate({ to: "/system-admin/admins" })}
        />

        <DashboardCard
          icon={ScrollText}
          label={td("cards.recentAudit.label")}
          value={recentAudit.data?.paging.total}
          isLoading={recentAudit.isLoading}
          accent="default"
          onClick={() => void navigate({ to: "/system-admin/audit" })}
        />
      </div>

      {/* Recent audit panel */}
      <Card variant="default" padding="none">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-3">
          <h2 className="text-sm font-semibold text-[var(--foreground)]">
            {td("cards.recentAudit.label")}
          </h2>
          <button
            type="button"
            className="text-xs text-[var(--primary)] hover:underline focus-visible:outline-none"
            onClick={() => void navigate({ to: "/system-admin/audit" })}
          >
            {td("recentAudit.viewAll")}
          </button>
        </div>

        <CardBody className="!p-0">
          {isMobile ? (
            <div className="space-y-3 p-3">
              {recentAudit.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-20 animate-pulse rounded-xl bg-[var(--muted)]" />
                ))
              ) : auditRows.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-[var(--muted-foreground)]">
                  {td("recentAudit.empty")}
                </p>
              ) : (
                auditRows.map((entry) => {
                  const actorName = entry.actorUserId
                    ? formatUserName(entry.actorUser ?? null, {
                        fallback: td("meta.unknown_actor"),
                      })
                    : td("meta.system_actor");

                  return (
                    <article key={entry.id} className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-3">
                      <div className="flex min-w-0 items-start gap-2">
                        {entry.actorUserId ? (
                          <UserRound
                            className="mt-0.5 size-4 shrink-0 text-[var(--muted-foreground)]"
                            aria-hidden
                          />
                        ) : (
                          <Globe
                            className="mt-0.5 size-4 shrink-0 text-[var(--muted-foreground)]"
                            aria-hidden
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-[var(--foreground)]">
                            {actorName}
                          </p>
                          <p className="mt-1 text-xs font-medium text-[var(--foreground)]">
                            {formatAuditActionLabel(entry.action, ta)}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--muted-foreground)]">
                            <span className="max-w-[12rem] truncate" title={resolveSystemAuditTargetSubtitle(entry)}>
                              {resolveSystemAuditTarget(entry)}
                            </span>
                            <time dateTime={entry.createdAt}>{fmtTime(entry.createdAt)}</time>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          ) : recentAudit.isLoading ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-[var(--border)]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3 sm:px-6">
                        <div className="h-4 w-40 rounded bg-[var(--muted)] animate-pulse" />
                      </td>
                      <td className="px-5 py-3 sm:px-6">
                        <div className="h-4 w-48 rounded bg-[var(--muted)] animate-pulse" />
                      </td>
                      <td className="px-5 py-3 sm:px-6">
                        <div className="h-4 w-28 rounded bg-[var(--muted)] animate-pulse" />
                      </td>
                      <td className="px-5 py-3 sm:px-6">
                        <div className="h-4 w-24 rounded bg-[var(--muted)] animate-pulse" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : auditRows.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-[var(--muted-foreground)]">
              {td("recentAudit.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[var(--secondary)]/40">
                  <tr className="border-b border-[var(--border)]">
                    <th className="whitespace-nowrap px-5 py-3 text-left text-xs font-medium text-[var(--muted-foreground)] sm:px-6">
                      {ta("columns.actor")}
                    </th>
                    <th className="whitespace-nowrap px-5 py-3 text-left text-xs font-medium text-[var(--muted-foreground)] sm:px-6">
                      {ta("columns.action")}
                    </th>
                    <th className="whitespace-nowrap px-5 py-3 text-left text-xs font-medium text-[var(--muted-foreground)] sm:px-6">
                      {ta("columns.target")}
                    </th>
                    <th className="whitespace-nowrap px-5 py-3 text-left text-xs font-medium text-[var(--muted-foreground)] sm:px-6">
                      {ta("columns.time")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {auditRows.map((entry) => {
                    const actorName = entry.actorUserId
                      ? formatUserName(entry.actorUser ?? null, {
                          fallback: td("meta.unknown_actor"),
                        })
                      : td("meta.system_actor");

                    return (
                      <tr key={entry.id} className="transition-colors hover:bg-[var(--accent)]/40">
                        <td className="px-5 py-3 align-middle sm:px-6">
                          <span className="inline-flex max-w-[220px] items-center gap-2">
                            {entry.actorUserId ? (
                              <UserRound
                                className="size-4 shrink-0 text-[var(--muted-foreground)]"
                                aria-hidden
                              />
                            ) : (
                              <Globe
                                className="size-4 shrink-0 text-[var(--muted-foreground)]"
                                aria-hidden
                              />
                            )}
                            <span className="truncate font-medium text-[var(--foreground)]">
                              {actorName}
                            </span>
                          </span>
                        </td>
                        <td className="px-5 py-3 align-middle sm:px-6">
                          <span className="font-medium text-[var(--foreground)]">
                            {formatAuditActionLabel(entry.action, ta)}
                          </span>
                        </td>
                        <td className="px-5 py-3 align-middle sm:px-6">
                          <span className="inline-flex max-w-[220px] flex-col text-[var(--muted-foreground)]">
                            <span className="truncate text-[var(--foreground)]" title={resolveSystemAuditTarget(entry)}>
                              {resolveSystemAuditTarget(entry)}
                            </span>
                            <span className="truncate text-xs" title={resolveSystemAuditTargetSubtitle(entry)}>
                              {resolveSystemAuditTargetSubtitle(entry)}
                            </span>
                          </span>
                        </td>
                        <td className="px-5 py-3 align-middle sm:px-6">
                          <time
                            dateTime={entry.createdAt}
                            className="whitespace-nowrap text-xs tabular-nums text-[var(--muted-foreground)]"
                          >
                            {fmtTime(entry.createdAt)}
                          </time>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
